import { EntityGrid, Typography, useDimensions, useFavorites, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef } from 'react'
import { continueCap } from './continue-items.js'
import { MusicLine } from './music-line.js'
import { PerformButton } from './perform-button.js'
import { songMusicLineParts } from './song-music-line.js'
import { StartTiles, type StartTilesProps } from './start-tiles.js'
import { useContinueItems } from './use-continue-items.js'
import { useIsSoloUser } from './use-is-solo-user.js'
import { useSectionPinShortcut } from './use-section-pin-shortcut.js'
import styles from './continue-section.module.css'

export type ContinueSectionProps = StartTilesProps

/**
 * Home's first shelf: what to pick up next. Pinned entities lead (in pin order, max 3 by the pin cap), then the
 * entities I last edited (`useRecentSplit().mine`), deduped and capped at six cards.
 *
 * Song cards carry their music line (key, tempo, length) and a Perform button that opens the Performance layout.
 *
 * On first run there is nothing to pick up, so the shelf becomes four equal-weight start tiles.
 */
export const ContinueSection: FC<ContinueSectionProps> = props => {
  const { items, loading, isEmpty } = useContinueItems()
  const { isPinned } = useFavorites()
  const navigateToEntity = useNavigateToEntity()
  const isSolo = useIsSoloUser()
  const { isSmallOrSmaller } = useDimensions()
  const sectionRef = useRef<HTMLElement>(null)

  useSectionPinShortcut({ sectionRef, entities: items })

  return (
    <section ref={sectionRef} className={styles.section} aria-labelledby="home-continue">
      <div className={styles.headingRow}>
        <Typography id="home-continue" variant="h2" className={styles.heading}>
          Continue
        </Typography>
        {isEmpty ? null : (
          <Typography variant="caption" className={styles.caption}>
            Pinned first, then what you last edited
          </Typography>
        )}
      </div>
      {isEmpty ? (
        <div className={styles.empty}>
          <div>
            <Typography variant="subtitle1" component="p" className={styles.emptyTitle}>
              Nothing to pick up yet
            </Typography>
            <Typography variant="body2" component="p" className={styles.emptyBody}>
              Start something and it waits for you here, so tomorrow begins where today stopped.
            </Typography>
          </div>
          <StartTiles {...props} />
          <Typography variant="caption" component="p" className={styles.hint}>
            Already have demos or charts? Drop them anywhere on this page.
          </Typography>
        </div>
      ) : (
        <EntityGrid
          size="medium"
          entities={items}
          loading={loading}
          skeletonItems={continueCap}
          onOpen={navigateToEntity}
          isItemPinned={entity => isPinned(entity.id)}
          alwaysShowSecondary
          showLastEditor={!isSolo}
          showParentSpace
          itemMeta={entity => {
            const parts = songMusicLineParts(entity)
            return parts.length > 0 ? <MusicLine parts={parts} /> : null
          }}
          itemAction={entity =>
            entity.type === 'song' ? <PerformButton entity={entity} compact={isSmallOrSmaller} /> : null
          }
        />
      )}
    </section>
  )
}

export default ContinueSection
